<template>
  <div class="pomodoro-stats" v-if="show">
    <div class="stats-overlay" @click="$emit('close')"></div>
    <div class="stats-panel">
      <div class="stats-header">
        <h2>{{ t('stats.title') }}</h2>
        <button class="close-btn" @click="$emit('close')"><Icon name="close" :size="20" /></button>
      </div>

      <div class="stats-content">
        <!-- 统计卡片 -->
        <div class="stats-cards">
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.today') }}</div>
            <div class="stat-value">{{ stats.today.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.today.duration) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.thisWeek') }}</div>
            <div class="stat-value">{{ stats.week.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.week.duration) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.thisMonth') }}</div>
            <div class="stat-value">{{ stats.month.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.month.duration) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.total') }}</div>
            <div class="stat-value">{{ stats.total.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.total.duration) }}</div>
          </div>
        </div>

        <!-- 趋势图表 -->
        <div class="chart-block">
          <h3>{{ t('stats.chart') }}</h3>
          <div ref="chartRef" class="chart-box"></div>
        </div>

        <!-- 最近记录 -->
        <div class="recent-records">
          <h3>{{ t('pomodoro.title') }}</h3>
          <div class="records-list">
            <div v-if="recentRecords.length === 0" class="no-data">
              {{ t('stats.noData') }}
            </div>
            <div 
              v-for="record in recentRecords" 
              :key="record.id" 
              class="record-item"
            >
              <div class="record-time">{{ formatRecordTime(record.startTime) }}</div>
              <div class="record-duration">{{ record.duration }}min</div>
              <div class="record-note">{{ record.note || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="stats-actions">
          <button class="action-btn export-btn" @click="handleExport">
            {{ t('common.export') }}
          </button>
          <button class="action-btn import-btn" @click="triggerImport">
            {{ t('common.import') }}
          </button>
          <input 
            ref="fileInput" 
            type="file" 
            accept=".json" 
            @change="handleImport" 
            style="display: none;"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue'
import { usePomodoro } from '../composables/usePomodoro'
import { useI18n } from '../composables/useI18n'
import Icon from './Icon.vue'

export default {
  components: { Icon },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { stats, records, exportData, importData } = usePomodoro()
    const { t } = useI18n()
    const fileInput = ref(null)
    const chartRef = ref(null)
    let chart = null

    const recentRecords = computed(() => {
      return records.value
        .filter(r => r.completed)
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
        .slice(0, 10)
    })

    const formatDuration = (minutes) => {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      if (hours > 0) {
        return `${hours}h ${mins}m`
      }
      return `${mins}m`
    }

    const formatRecordTime = (time) => {
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const handleExport = () => {
      exportData()
    }

    const triggerImport = () => {
      fileInput.value.click()
    }

    const handleImport = async (event) => {
      const file = event.target.files[0]
      if (file) {
        try {
          await importData(file)
          alert(t('common.success') || '导入成功！页面将刷新以应用设置。')
          event.target.value = ''
          window.location.reload()
        } catch (error) {
          alert(t('common.error') || '导入失败：' + error.message)
        }
      }
    }

    const buildLast7 = () => {
      const now = new Date()
      const days = []
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(now.getDate() - i)
        const key = d.toISOString().slice(0, 10)
        days.push({ key, label: `${d.getMonth()+1}/${d.getDate()}`, count: 0 })
      }
      const map = new Map(days.map(d => [d.key, d]))
      records.value.filter(r => r.completed).forEach(r => {
        const key = new Date(r.startTime).toISOString().slice(0,10)
        if (map.has(key)) map.get(key).count += 1
      })
      return days
    }

    const renderChart = async () => {
      if (!chartRef.value) {
        console.warn('Chart ref not available')
        return
      }
      try {
        if (!chart) {
          const echartsModule = await import(/* webpackChunkName: "echarts" */ 'echarts')
          const echarts = echartsModule.default || echartsModule
          chart = echarts.init(chartRef.value)
          console.log('✓ ECharts initialized')
        }
        const data = buildLast7()
        chart.setOption({
          tooltip: { 
            trigger: 'axis',
            backgroundColor: 'rgba(28, 28, 32, 0.9)',
            borderColor: 'rgba(255, 255, 255, 0.08)',
            textStyle: { color: '#e8e8ec' }
          },
          xAxis: { 
            type: 'category', 
            data: data.map(d => d.label),
            axisLabel: { color: '#636366', fontSize: 11 },
            axisLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
            axisTick: { show: false }
          },
          yAxis: { 
            type: 'value', 
            minInterval: 1,
            axisLabel: { color: '#636366', fontSize: 11 },
            splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)' } }
          },
          textStyle: { color: '#a8a8b0' },
          grid: { containLabel: true, top: 10, right: 10, bottom: 10, left: 10 },
          series: [{
            type: 'bar',
            data: data.map(d => d.count),
            itemStyle: { 
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: '#63c97a' },
                  { offset: 1, color: '#4aad62' }
                ]
              },
              borderRadius: [4, 4, 0, 0]
            },
            barWidth: '40%'
          }]
        })
        console.log('✓ Chart rendered with data:', data)
      } catch (err) {
        console.error('Chart rendering error:', err)
      }
    }

    onMounted(() => {
      renderChart()
    })

    watch(records, () => renderChart())

    return {
      stats,
      recentRecords,
      fileInput,
      chartRef,
      t,
      formatDuration,
      formatRecordTime,
      handleExport,
      triggerImport,
      handleImport
    }
  }
}
</script>

<style lang="less" scoped>
.pomodoro-stats {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
}

.stats-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.stats-panel {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(28, 28, 32, 0.85);
  backdrop-filter: blur(24px) saturate(1.2);
  -webkit-backdrop-filter: blur(24px) saturate(1.2);
  border-radius: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06);
  animation: statsSlideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 768px) {
    width: 95%;
    max-height: 95vh;
    border-radius: 16px;
  }
}

@keyframes statsSlideUp {
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  h2 {
    margin: 0;
    color: #e8e8ec;
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.02em;

    @media (max-width: 768px) {
      font-size: 18px;
    }
  }

  .close-btn {
    background: rgba(255, 255, 255, 0.06);
    border: none;
    color: #8b8b94;
    font-size: 20px;
    cursor: pointer;
    padding: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
    }
  }
}

.stats-content {
  padding: 20px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

.stat-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 20px;
  border-radius: 14px;
  text-align: center;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .stat-label {
    color: #8b8b94;
    font-size: 13px;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 500;
  }

  .stat-value {
    color: #e8e8ec;
    font-size: 36px;
    font-weight: 700;
    margin-bottom: 4px;
    letter-spacing: -0.03em;
  }

  .stat-sub {
    color: #636366;
    font-size: 12px;
    font-weight: 500;
  }
}

.recent-records {
  margin-bottom: 24px;

  h3 {
    color: #e8e8ec;
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
  }

  .records-list {
    max-height: 300px;
    overflow-y: auto;
  }

  .no-data {
    text-align: center;
    color: #636366;
    padding: 40px 20px;
    font-size: 14px;
  }

  .record-item {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.04);
    border-radius: 10px;
    margin-bottom: 8px;
    gap: 16px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }

    @media (max-width: 768px) {
      flex-wrap: wrap;
      gap: 8px;
    }

    .record-time {
      color: #8b8b94;
      font-size: 13px;
      min-width: 100px;
    }

    .record-duration {
      color: #63c97a;
      font-weight: 600;
      min-width: 60px;
      font-size: 14px;
    }

    .record-note {
      color: #a8a8b0;
      flex: 1;
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.chart-block {
  margin-bottom: 24px;

  h3 {
    color: #e8e8ec;
    margin: 0 0 12px 0;
    font-weight: 600;
    font-size: 16px;
  }

  .chart-box {
    width: 100%;
    height: 240px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.04);
    border-radius: 14px;
  }
}

.stats-actions {
  display: flex;
  gap: 12px;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  .action-btn {
    padding: 12px 28px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.25s ease;

    @media (max-width: 768px) {
      width: 100%;
    }

    &.export-btn {
      background: linear-gradient(135deg, #63c97a, #4aad62);
      color: #fff;
      box-shadow: 0 4px 12px rgba(99, 201, 122, 0.25);

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(99, 201, 122, 0.35);
      }

      &:active {
        transform: translateY(0);
      }
    }

    &.import-btn {
      background: rgba(255, 255, 255, 0.08);
      color: #a8a8b0;
      border: 1px solid rgba(255, 255, 255, 0.1);

      &:hover {
        background: rgba(255, 255, 255, 0.14);
        color: #e8e8ec;
      }
    }
  }
}
</style>
